<template>
    <v-dialog
        v-model="isOpen"
        width="80vw"
        height="80vh"
        max-width="1200"
        persistent
    >
        <v-card class="settings-modal">
            <v-card-title class="primary white--text d-flex align-center">
                <v-icon left color="white">mdi-cog</v-icon>
                Einstellungen
                <v-spacer></v-spacer>
                <v-btn 
                    text 
                    color="white" 
                    @click="goToOldVersion"
                    class="mr-2"
                >
                    <v-icon left>mdi-history</v-icon>
                    Alte Version
                </v-btn>
                <v-btn icon dark @click="closeModal">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            
            <v-tabs v-model="activeTab" color="primary" class="settings-tabs">
                <v-tab key="printer">
                    <v-icon left>mdi-printer-settings</v-icon>
                    Drucker
                </v-tab>
                <v-tab key="admin">
                    <v-icon left>mdi-shield-account</v-icon>
                    Admin
                </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab" class="settings-content">
                <!-- Printer Settings Tab -->
                <v-tab-item key="printer">
                    <v-card-text class="pa-6 printer-content">
                <div class="mb-4">
                    <p class="text-body-1 grey--text text--darken-1">
                        Wählen Sie den Drucker für den Etikettendruck aus:
                    </p>
                    <p class="text-caption grey--text" v-if="printers.length > 0">
                        {{ printers.length }} Drucker gefunden
                    </p>
                </div>

                <div v-if="loading" class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                    <p class="mt-4 text-body-1 grey--text">Drucker werden geladen...</p>
                </div>

                <div v-else-if="printers.length === 0" class="text-center py-8">
                    <v-icon size="64" color="grey">mdi-printer-off</v-icon>
                    <p class="mt-4 text-h6 grey--text">Keine Drucker gefunden</p>
                    <v-btn color="primary" @click="getPrinters" class="mt-2">
                        <v-icon left>mdi-refresh</v-icon>
                        Erneut versuchen
                    </v-btn>
                </div>

                <div v-else class="printer-grid">
                    <v-card 
                        v-for="printer in printers" 
                        :key="printer._id"
                        class="printer-card"
                        :class="{ 'selected': selectedPrinter && selectedPrinter._id === printer._id }"
                        @click="selectPrinter(printer)"
                        elevation="2"
                    >
                        <v-card-text class="pa-4">
                            <div class="d-flex align-center mb-3">
                                <v-avatar :color="printer.online ? 'success' : 'error'" size="40" class="mr-3">
                                    <v-icon color="white">
                                        {{ printer.online ? 'mdi-printer' : 'mdi-printer-off' }}
                                    </v-icon>
                                </v-avatar>
                                
                                <div class="flex-grow-1">
                                    <h3 class="text-h6 font-weight-medium mb-1">{{ printer.name }}</h3>
                                    <v-chip 
                                        :color="printer.online ? 'success' : 'error'" 
                                        small 
                                        text-color="white"
                                        class="font-weight-medium"
                                    >
                                        <v-icon left small>
                                            {{ printer.online ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                        </v-icon>
                                        {{ printer.online ? 'Online' : 'Offline' }}
                                    </v-chip>
                                </div>
                                
                                <v-radio 
                                    :input-value="selectedPrinter && selectedPrinter._id === printer._id"
                                    @click.stop="selectPrinter(printer)"
                                    color="primary" 
                                    class="ma-0"
                                ></v-radio>
                            </div>
                            

                        </v-card-text>
                    </v-card>
                    </div>
                    </v-card-text>
                    
                    <v-divider></v-divider>
                    
                    <v-card-actions class="pa-4">
                        <v-btn @click="getPrinters" text color="grey darken-1" :loading="loading">
                            <v-icon left>mdi-refresh</v-icon>
                            Aktualisieren
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn @click="closeModal" text color="grey darken-1">
                            Abbrechen
                        </v-btn>
                        <v-btn 
                            @click="confirmSelection" 
                            color="primary" 
                            :disabled="!selectedPrinter"
                        >
                            <v-icon left>mdi-check</v-icon>
                            Bestätigen
                        </v-btn>
                    </v-card-actions>
                </v-tab-item>

                <!-- Layout Settings Tab -->

                <!-- Admin Settings Tab -->
                <v-tab-item key="admin" v-if="user.role === 'admin'">
                    <v-card-text class="pa-6">
                        <div class="admin-header mb-6">
                            <div class="d-flex align-center mb-3">
                                <v-avatar color="primary" size="48" class="mr-4">
                                    <v-icon color="white" size="28">mdi-shield-account</v-icon>
                                </v-avatar>
                                <div>
                                    <h2 class="text-h5 font-weight-bold mb-1">Admin-Bereich</h2>
                                    <p class="text-subtitle-1 grey--text mb-0">
                                        Erweiterte Funktionen und Systemverwaltung
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Packlist Reset Section -->
                        <div class="admin-section">
                            <v-card outlined class="admin-card">
                                <v-card-text class="pa-6">
                                    <div class="d-flex align-center justify-space-between">
                                        <div class="flex-grow-1 mr-4">
                                            <div class="d-flex align-center mb-3">
                                                <v-icon color="error" size="32" class="mr-3">mdi-refresh</v-icon>
                                                <h3 class="text-h6 font-weight-bold">
                                                    Packliste zurücksetzen
                                                </h3>
                                            </div>
                                            <p class="text-body-2 grey--text text--darken-1 mb-2">
                                                Setzt alle Artikel der aktuellen Packliste zurück. 
                                            </p>
                                            <p class="text-caption error--text">
                                                ⚠️ Alle Packfortschritte gehen unwiderruflich verloren!
                                            </p>
                                        </div>
                                        <v-btn 
                                            color="error" 
                                            large
                                            @click="resetPacklist"
                                            :disabled="resetInProgress"
                                            :loading="resetInProgress"
                                        >
                                            <v-icon left>mdi-refresh</v-icon>
                                            Zurücksetzen
                                        </v-btn>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>
                    </v-card-text>
                </v-tab-item>
            </v-tabs-items>
        </v-card>
    </v-dialog>
</template>

<script>
import PacklistService from '@/services/PacklistService';

export default {
    name: 'SettingsModal',
    data() {
        return {
            printers: [],
            selectedPrinter: null,
            isOpen: true,
            loading: false,
            activeTab: 0,
            resetInProgress: false
        }
    },
    computed: {
        packlistName() {
            return this.$route.params.id;
        }
    },
    props: {
        currentPrinter: {
            type: Object,
            default: null
        },
        openPrinterTab: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object,
            default: null
        }
    },
    methods: {
        async getPrinters() {
            this.loading = true;
            try {
                const response = await PacklistService.getPrinters();
                this.printers = response.data || [];
            } catch (error) {
                console.error('Error getting printers:', error);
                this.printers = [];
            } finally {
                this.loading = false;
            }
        },
        selectPrinter(printer) {
            this.selectedPrinter = printer;
        },
        confirmSelection() {
            if (this.selectedPrinter) {
                this.$emit('select-printer', this.selectedPrinter);
                this.closeModal();
            }
        },
        closeModal() {
            this.isOpen = false;
            this.$emit('close-modal');
        },
        goToOldVersion() {
            // Navigate to old packlist version
            const currentPacklistId = this.$route.params.id;
            this.$router.push(`/packlistV2/${currentPacklistId}`);
        },
        async resetPacklist() {
            try {
                this.resetInProgress = true;
                console.log('🔄 Starting packlist reset for:', this.packlistName);

                const response = await PacklistService.resetPacklist(this.packlistName);

                if (response.status === 200) {
                    console.log('✅ Packlist reset successful');
                    
                    // Close modal and emit events for refresh
                    this.$router.push("/");
                    
                } else {
                    console.error('❌ Packlist reset failed:', response);
                }
            } catch (error) {
                console.error('❌ Error during packlist reset:', error);
            } finally {
                this.resetInProgress = false;
            }
        }
    },
    watch: {
        isOpen(newVal) {
            if (!newVal) {
                this.$emit('close-modal');
            }
        }
    },
    async mounted() {
        await this.getPrinters();

        if (this.currentPrinter) {
            this.selectedPrinter = this.currentPrinter;
        }

        // Auto-open printer tab if no printer is selected or openPrinterTab prop is true
        if (this.openPrinterTab || !this.currentPrinter) {
            this.activeTab = 0; // Printer tab
        }
    }
}
</script>

<style scoped>
.settings-modal {
    border-radius: 8px;
    overflow: hidden;
}

.settings-tabs {
    border-bottom: 1px solid #e0e0e0;
}

.settings-content {
    max-height: 70vh;
    overflow-y: auto;
}

.printer-content {
    max-height: 60vh;
    overflow-y: auto;
}

.printer-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 16px;
    margin-top: 16px;
    max-height: none;
    overflow: visible;
}

.printer-card {
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

.layout-options {
    margin-top: 16px;
}

.layout-radio {
    margin-bottom: 16px !important;
}

.layout-option {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    transition: all 0.2s ease;
    width: 100%;
    min-width: 300px;
}

.layout-option:hover {
    border-color: #1976d2;
    background-color: #f5f5f5;
}

.layout-icon {
    flex-shrink: 0;
}

.layout-details {
    flex: 1;
}

.printer-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.printer-card.selected {
    border-color: #1976d2;
    background-color: rgba(25, 118, 210, 0.05);
    box-shadow: 0 3px 10px rgba(25, 118, 210, 0.2) !important;
}



.v-chip {
    font-size: 11px;
    height: 24px;
}

.v-radio-group {
    flex-shrink: 0;
}

.v-btn {
    text-transform: none;
}

/* Admin tab styling */
.admin-header {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 16px;
}

.admin-section {
    margin-bottom: 24px;
}

.admin-card {
    border-radius: 12px;
    transition: all 0.3s ease;
}

.admin-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .printer-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
    
    .printer-card .v-card-text {
        padding: 16px !important;
    }
}

@media (max-width: 480px) {
    .printer-card .d-flex {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .printer-card .v-radio-group {
        align-self: flex-end;
        margin-top: 8px;
    }
}
</style>